import {
  ChatNotesAttachment,
  ChatNotesAttachmentKind,
  ChatNotesCoreStatus,
  ChatNotesCurrentSession,
  ChatNotesDraft,
  ChatNotesFolder,
  ChatNotesIssuedSession,
  ChatNotesNote,
  ChatNotesNotePage,
  ChatNotesNotificationSettings,
  ChatNotesFormatting,
  parseCoreStatus,
  parseAttachmentResult,
  parseCurrentSession,
  parseDeletedFolder,
  parseDeletedNote,
  parseDraftMutation,
  parseDrafts,
  parseFolderResult,
  parseFolders,
  parseNoteResult,
  parseNotes,
  parseIssuedSession,
  parseNotificationSettingsResult,
  parseRevokedSession
} from './contract';
import {ChatNotesError} from './errors';

const DEFAULT_TIMEOUT_MS = 5000;
const LOCAL_HTTP_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

export interface ChatNotesClientOptions {
  baseUrl: string;
  fetch?: typeof fetch;
  timeoutMs?: number;
}

export interface ChatNotesNoteInput {
  folderId?: string;
  content: string;
  contentFormat?: 'plain' | 'markdown';
  attachmentId?: string;
  groupId?: string;
  formatting?: ChatNotesFormatting[];
  replyToNoteId?: string;
  forwardedFromNoteId?: string;
  isPinned?: boolean;
  reactions?: string[];
  remindAt?: string;
}

export interface ChatNotesDraftInput {
  folderId?: string;
  content: string;
  formatting?: ChatNotesFormatting[];
  replyToNoteId?: string;
}

function normalizeBaseUrl(value: string): string {
  let url: URL;

  try {
    url = new URL(value);
  } catch(cause) {
    throw new ChatNotesError('INVALID_CONFIGURATION', 'Chat Notes Core URL is invalid.', {cause});
  }

  const localHttp = url.protocol === 'http:' && LOCAL_HTTP_HOSTS.has(url.hostname);
  if(url.protocol !== 'https:' && !localHttp) {
    throw new ChatNotesError(
      'INVALID_CONFIGURATION',
      'Chat Notes Core must use HTTPS, except on localhost.'
    );
  }

  if(url.username || url.password || url.search || url.hash) {
    throw new ChatNotesError(
      'INVALID_CONFIGURATION',
      'Chat Notes Core URL must not contain credentials, query parameters, or a fragment.'
    );
  }

  url.pathname = url.pathname.replace(/\/+$/, '');
  return url.toString().replace(/\/$/, '');
}

function requestIdFrom(response: Response, payload: unknown): string | undefined {
  const header = response.headers.get('x-request-id');
  if(header) return header;

  if(typeof(payload) !== 'object' || payload === null || !('meta' in payload)) return undefined;
  const meta = (payload as {meta?: unknown}).meta;
  if(typeof(meta) !== 'object' || meta === null || !('requestId' in meta)) return undefined;
  const requestId = (meta as {requestId?: unknown}).requestId;
  return typeof(requestId) === 'string' ? requestId : undefined;
}

function serverCodeFrom(payload: unknown): string | undefined {
  if(typeof(payload) !== 'object' || payload === null || !('error' in payload)) return undefined;
  const error = (payload as {error?: unknown}).error;
  if(typeof(error) !== 'object' || error === null || !('code' in error)) return undefined;
  const code = (error as {code?: unknown}).code;
  return typeof(code) === 'string' ? code : undefined;
}

export class ChatNotesClient {
  private readonly baseUrl: string;
  private readonly fetch: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: ChatNotesClientOptions) {
    this.baseUrl = normalizeBaseUrl(options.baseUrl);
    this.fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    if(!Number.isFinite(this.timeoutMs) || this.timeoutMs <= 0) {
      throw new ChatNotesError('INVALID_CONFIGURATION', 'Chat Notes request timeout must be positive.');
    }
  }

  private async request(path: string, init: RequestInit): Promise<unknown> {
    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.timeoutMs);

    let response: Response;
    try {
      response = await this.fetch(`${this.baseUrl}${path}`, {
        cache: 'no-store',
        credentials: 'omit',
        ...init,
        headers: {accept: 'application/json', ...init.headers},
        signal: controller.signal
      });
    } catch(cause) {
      clearTimeout(timeout);
      throw new ChatNotesError(
        timedOut ? 'TIMEOUT' : 'NETWORK_ERROR',
        timedOut ? 'Chat Notes Core did not respond in time.' : 'Could not reach Chat Notes Core.',
        {cause}
      );
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch(cause) {
      clearTimeout(timeout);
      if(timedOut) {
        throw new ChatNotesError(
          'TIMEOUT',
          'Chat Notes Core did not respond in time.',
          {cause, requestId: response.headers.get('x-request-id') ?? undefined}
        );
      }

      throw new ChatNotesError(
        'INVALID_RESPONSE',
        'Chat Notes Core returned invalid JSON.',
        {cause, requestId: response.headers.get('x-request-id') ?? undefined, status: response.status}
      );
    }
    clearTimeout(timeout);

    const requestId = requestIdFrom(response, payload);
    if(!response.ok) {
      throw new ChatNotesError(
        'HTTP_ERROR',
        `Chat Notes Core returned HTTP ${response.status}.`,
        {requestId, serverCode: serverCodeFrom(payload), status: response.status}
      );
    }

    return payload;
  }

  public async getStatus(): Promise<ChatNotesCoreStatus> {
    return parseCoreStatus(await this.request('/api/v1/status', {method: 'GET'})).data;
  }

  public async createTelegramSession(initData: string): Promise<ChatNotesIssuedSession> {
    const payload = await this.request('/api/v1/auth/telegram', {
      body: JSON.stringify({initData}),
      headers: {'content-type': 'application/json'},
      method: 'POST'
    });
    return parseIssuedSession(payload).data;
  }

  public async createDevelopmentSession(): Promise<ChatNotesIssuedSession> {
    const payload = await this.request('/api/v1/auth/development', {method: 'POST'});
    return parseIssuedSession(payload).data;
  }

  public async getSession(token: string): Promise<ChatNotesCurrentSession> {
    const payload = await this.request('/api/v1/session', {
      headers: {authorization: `Bearer ${token}`},
      method: 'GET'
    });
    return parseCurrentSession(payload).data;
  }

  public async revokeSession(token: string): Promise<void> {
    const payload = await this.request('/api/v1/session', {
      headers: {authorization: `Bearer ${token}`},
      method: 'DELETE'
    });
    parseRevokedSession(payload);
  }


  public async listFolders(token: string): Promise<ChatNotesFolder[]> {
    const payload = await this.request('/api/v1/folders', {
      headers: {authorization: `Bearer ${token}`},
      method: 'GET'
    });
    return parseFolders(payload).data.folders;
  }

  public async createFolder(
    token: string,
    input: {title: string; icon?: string; preferredFolderNumber?: number}
  ): Promise<ChatNotesFolder> {
    const payload = await this.request('/api/v1/folders', {
      body: JSON.stringify(input),
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      },
      method: 'POST'
    });
    return parseFolderResult(payload).data.folder;
  }

  public async updateFolder(
    token: string,
    folderId: string,
    input: {title: string; icon?: string; revision: number}
  ): Promise<ChatNotesFolder> {
    const payload = await this.request(`/api/v1/folders/${encodeURIComponent(folderId)}`, {
      body: JSON.stringify(input),
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      },
      method: 'PATCH'
    });
    return parseFolderResult(payload).data.folder;
  }

  public async deleteFolder(token: string, folderId: string): Promise<void> {
    const payload = await this.request(`/api/v1/folders/${encodeURIComponent(folderId)}`, {
      headers: {authorization: `Bearer ${token}`},
      method: 'DELETE'
    });
    parseDeletedFolder(payload);
  }

  public async reorderFolders(token: string, folderIds: string[]): Promise<ChatNotesFolder[]> {
    const payload = await this.request('/api/v1/folders/reorder', {
      body: JSON.stringify({folderIds}),
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      },
      method: 'PUT'
    });
    return parseFolders(payload).data.folders;
  }

  public async listNotes(
    token: string,
    options: {folderId?: string; cursor?: string; limit?: number} = {}
  ): Promise<ChatNotesNotePage> {
    const query = new URLSearchParams();
    if(options.folderId) query.set('folderId', options.folderId);
    if(options.cursor) query.set('cursor', options.cursor);
    if(options.limit !== undefined) query.set('limit', String(options.limit));
    const suffix = query.size ? `?${query}` : '';
    const payload = await this.request(`/api/v1/notes${suffix}`, {
      headers: {authorization: `Bearer ${token}`},
      method: 'GET'
    });
    return parseNotes(payload).data;
  }

  public async getNote(token: string, noteId: string): Promise<ChatNotesNote> {
    const payload = await this.request(`/api/v1/notes/${encodeURIComponent(noteId)}`, {
      headers: {authorization: `Bearer ${token}`},
      method: 'GET'
    });
    return parseNoteResult(payload).data.note;
  }

  public async createNote(token: string, input: ChatNotesNoteInput): Promise<ChatNotesNote> {
    const payload = await this.request('/api/v1/notes', {
      body: JSON.stringify(input),
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      },
      method: 'POST'
    });
    return parseNoteResult(payload).data.note;
  }

  public async updateNote(
    token: string,
    noteId: string,
    input: ChatNotesNoteInput & {revision: number}
  ): Promise<ChatNotesNote> {
    const payload = await this.request(`/api/v1/notes/${encodeURIComponent(noteId)}`, {
      body: JSON.stringify(input),
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      },
      method: 'PATCH'
    });
    return parseNoteResult(payload).data.note;
  }

  public async deleteNote(token: string, noteId: string): Promise<void> {
    const payload = await this.request(`/api/v1/notes/${encodeURIComponent(noteId)}`, {
      headers: {authorization: `Bearer ${token}`},
      method: 'DELETE'
    });
    parseDeletedNote(payload);
  }

  public async listDrafts(token: string): Promise<ChatNotesDraft[]> {
    const payload = await this.request('/api/v1/drafts', {
      headers: {authorization: `Bearer ${token}`},
      method: 'GET'
    });
    return parseDrafts(payload).data.drafts;
  }

  public async saveDraft(token: string, input: ChatNotesDraftInput): Promise<void> {
    const payload = await this.request('/api/v1/drafts', {
      body: JSON.stringify(input),
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      },
      method: 'PUT'
    });
    parseDraftMutation(payload);
  }

  public async getNotificationSettings(
    token: string,
    scope: string
  ): Promise<ChatNotesNotificationSettings> {
    const payload = await this.request(
      `/api/v1/notification-settings?scope=${encodeURIComponent(scope)}`,
      {headers: {authorization: `Bearer ${token}`}, method: 'GET'}
    );
    return parseNotificationSettingsResult(payload).data.settings;
  }

  public async saveNotificationSettings(
    token: string,
    input: Pick<ChatNotesNotificationSettings, 'scope' | 'showPreviews' | 'muteUntil'>
  ): Promise<ChatNotesNotificationSettings> {
    const payload = await this.request('/api/v1/notification-settings', {
      body: JSON.stringify(input),
      headers: {authorization: `Bearer ${token}`, 'content-type': 'application/json'},
      method: 'PUT'
    });
    return parseNotificationSettingsResult(payload).data.settings;
  }

  public async uploadAttachment(
    token: string,
    kind: ChatNotesAttachmentKind,
    file: Blob,
    fileName: string
  ): Promise<ChatNotesAttachment> {
    const body = new FormData();
    body.set('kind', kind);
    body.set('file', file, fileName);
    const payload = await this.request('/api/v1/attachments', {
      body,
      headers: {authorization: `Bearer ${token}`},
      method: 'POST'
    });
    return parseAttachmentResult(payload).data.attachment;
  }

  public async getAttachment(token: string, attachmentId: string): Promise<ChatNotesAttachment> {
    const payload = await this.request(`/api/v1/attachments/${encodeURIComponent(attachmentId)}`, {
      headers: {authorization: `Bearer ${token}`},
      method: 'GET'
    });
    return parseAttachmentResult(payload).data.attachment;
  }

  public async downloadAttachment(token: string, attachmentId: string): Promise<Uint8Array> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetch(
        `${this.baseUrl}/api/v1/attachments/${encodeURIComponent(attachmentId)}/content`,
        {
          cache: 'no-store',
          credentials: 'omit',
          headers: {authorization: `Bearer ${token}`},
          signal: controller.signal
        }
      );
      if(!response.ok) {
        throw new ChatNotesError('HTTP_ERROR', `Chat Notes Core returned HTTP ${response.status}.`, {
          requestId: response.headers.get('x-request-id') ?? undefined,
          status: response.status
        });
      }
      return new Uint8Array(await response.arrayBuffer());
    } catch(cause) {
      if(cause instanceof ChatNotesError) throw cause;
      throw new ChatNotesError(
        controller.signal.aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
        controller.signal.aborted ? 'Chat Notes Core did not respond in time.' :
          'Could not download the Chat Notes attachment.',
        {cause}
      );
    } finally {
      clearTimeout(timeout);
    }
  }
}
